/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { Button } from 'reactstrap';
import { FlowSprMission } from '../../../../../services/types/Flow';
import { customDateStr } from '../../../../../shared/misc/utils';

export interface SprFormData {
  supervisionDateFrom: Date | null;
  supervisionDateTo: Date | null;
  fileName: string;
  rating: string | null;
  rationale: string | null;
  missions: FlowSprMission[];
  missionId: number | null;
  flowType: string;
}

interface Props {
  handleSubmit: (values: SprFormData, resetFormCallback: () => void) => void;
  handleCancel: () => void;
  formState?: SprFormData;
}

const SprForm = (props: Props) => {
  const { handleSubmit, handleCancel, formState } = props;
  const { t } = useTranslation();
  const formInit: SprFormData = {
    supervisionDateFrom: formState ? formState.supervisionDateFrom : null,
    supervisionDateTo: formState ? formState.supervisionDateTo : null,
    fileName: formState ? formState.fileName : '',
    rating: formState ? formState.rating : '',
    rationale: formState ? formState.rationale : '',
    missionId: formState ? formState.missionId : null,
    missions: formState ? formState.missions : [],
    flowType: formState ? formState.flowType : '',
  };

  const setMissionDate = (
    formik: FormikProps<SprFormData>,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const misionId = Number(e.currentTarget.value);

    if (misionId === null || misionId < 0) {
      formik.setFieldValue('missionId', null);
      return;
    }

    if (misionId === 0) {
      setTimeout(() => {
        formik.setFieldValue('missionId', misionId);
        formik.setFieldValue('supervisionDateFrom', null);
        formik.setFieldValue('supervisionDateTo', null);
      });
    } else {
      const mission = formik.values.missions.find((x) => x.id === misionId);
      if (mission !== undefined) {
        setTimeout(() => {
          formik.setFieldValue('missionId', misionId);
          formik.setFieldValue('supervisionDateFrom', new Date(mission.startDate));
          formik.setFieldValue('supervisionDateTo', new Date(mission.endDate));
          formik.setTouched({ ...formik.touched, supervisionDateFrom: true });
          formik.setTouched({ ...formik.touched, supervisionDateTo: true });
        });
      }
    }
  };

  const sprFormSchema = Yup.object().shape({
    supervisionDateFrom: Yup.date().required(t('required')).typeError(t('required')),
    supervisionDateTo: Yup.date()
      .required(t('required'))
      .typeError(t('required'))
      .min(Yup.ref('supervisionDateFrom'), () => `must be after Date From.`),
    rating: Yup.string()
      .nullable()
      .required(t('required'))
      .test(
        'rating-change',
        'The SPR rating in the report is different that the one provided here. Please make sure the SPR is consistent.​',
        (value) => formInit.rating === value || formInit.flowType !== 'SR_ESPF'
      ),
    rationale: Yup.string()
      .required(t('required'))
      .test(
        'rationale-change',
        'The SPR rationale in the report is different that the one provided here. Please make sure the SPR is consistent.​',
        (value) => formInit.rationale === value || formInit.flowType !== 'SR_ESPF'
      ),
    missionId: Yup.number().required(t('required')).nullable(),
  });

  return (
    <Formik
      initialValues={formInit}
      validationSchema={sprFormSchema}
      validateOnChange
      onSubmit={(values, { resetForm }) => {
        const payload = { ...values } as SprFormData;
        payload.missionId = Number(payload.missionId);
        handleSubmit(payload, resetForm);
      }}
      onReset={() => {
        // Formik's resetForm() already invokes this onReset handler itself
        // (see formik/dist/formik.cjs.development.js) - calling resetForm()
        // again from in here re-enters it and recurses until the call stack
        // overflows. handleReset (bound to the form's native reset event)
        // already calls resetForm() before this runs, so nothing here needs
        // to reset the form again.
        handleCancel();
      }}
    >
      {(formik) => (
        <form className="form" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
          <div className="modal__body">
            <div className="spr-modal modal-dialog--custom-filter">
              <div className="form-row">
                <div className="col-sm-12">
                  <h4 className="bold-text modal__title">{t('finalRating')}</h4>
                </div>
              </div>

              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">
                    {t('missionsRelated')}
                    <span className="error"> *</span>
                  </label>
                  <div className="select-wrapper">
                    <Field
                      as="select"
                      id="missionId"
                      name="missionId"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setMissionDate(formik, e)
                      }
                      className="dropdown-spr"
                    >
                      <option value="">{t('selectOption')}</option>
                      {formik.values.missions.map((x) => (
                        <option value={x.id}>
                          {x.number}
                          {t(' From ') +
                            customDateStr(x.startDate.toString()) +
                            t(' to ') +
                            customDateStr(x.endDate.toString())}
                        </option>
                      ))}
                      <option value={0}>{t('There is no mission related')}</option>
                    </Field>
                  </div>
                </div>
                {formik.touched.missionId && formik.errors.missionId && (
                  <div className="col-sm-12">
                    <span className="form__form-group-error">{formik.errors.missionId}</span>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">
                    {t('Supervision dates')}
                    <span className="error"> *</span>
                  </label>
                  <div className="input-group input-group-date-picker-wrapper">
                    <div className="form__form-group-field form__date-picker-wrapper shared-2">
                      <DatePicker
                        autoComplete="off"
                        id="supervisionDateFrom"
                        name="supervisionDateFrom"
                        selected={formik.values.supervisionDateFrom}
                        onChange={(date: Date | null) =>
                          formik.setFieldValue('supervisionDateFrom', date)
                        }
                        onBlur={() => {
                          formik.setTouched({ ...formik.touched, supervisionDateFrom: true });
                        }}
                        dateFormat="dd/MM/yyy"
                        className={
                          (formik.values.missionId === 0 &&
                          formik.values.supervisionDateFrom === null
                            ? 'input-date-picker-required-background-color'
                            : '') + t(' form-control-date-picker')
                        }
                        placeholderText={t('from')}
                        disabled={formik.values.missionId !== null && formik.values.missionId > 0}
                      />
                      <div className="form__form-group-icon">
                        <FontAwesomeIcon icon={faCalendar} />
                      </div>
                    </div>
                    <div className="form-row dropdown-spr">
                      <div className="col-sm-6">
                        {formik.errors.supervisionDateFrom && (
                          <span className="form__form-group-error">
                            {formik.errors.supervisionDateFrom}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="form__form-group-field form__date-picker-wrapper shared-2">
                      <DatePicker
                        autoComplete="off"
                        id="supervisionDateTo"
                        name="supervisionDateTo"
                        selected={formik.values.supervisionDateTo}
                        onChange={(date: Date | null) =>
                          formik.setFieldValue('supervisionDateTo', date)
                        }
                        onBlur={() => {
                          formik.setTouched({ ...formik.touched, supervisionDateTo: true });
                        }}
                        dateFormat="dd/MM/yyy"
                        className={
                          (formik.values.missionId === 0 && formik.values.supervisionDateTo === null
                            ? 'input-date-picker-required-background-color'
                            : '') + t(' form-control-date-picker')
                        }
                        placeholderText={t('to')}
                        disabled={formik.values.missionId !== null && formik.values.missionId > 0}
                      />
                      <div className="form__form-group-icon">
                        <FontAwesomeIcon icon={faCalendar} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="col-sm-6">
                  {formik.touched.supervisionDateTo && formik.errors.supervisionDateTo && (
                    <span className="form__form-group-error">
                      {formik.errors.supervisionDateTo}
                    </span>
                  )}
                </div>
              </div>

              {/*                   <div className="form-row">
                    <div className="col-sm-12">
                      <label className="col-form-label">{t('fileName')}</label>
                      <p className="form__text--readonly">{formik.values.fileName}</p>
                    </div>
                  </div> */}
              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">
                    {t('ratingField')}
                    <span className="error"> *</span>
                  </label>
                  <div className="select-wrapper">
                    <Field as="select" id="rating" name="rating" className="dropdown-spr">
                      <option value="">{t('selectOption')}</option>
                      <option value="1Satisfactory">{t('sfpCodes.1Satisfactory')}</option>
                      <option value="2Partially Satisfactory">
                        {t('sfpCodes.2Partially Satisfactory')}
                      </option>
                      <option value="3Partially Unsatisfactory">
                        {t('sfpCodes.3Partially Unsatisfactory')}
                      </option>
                      <option value="4Unsatisfactory">{t('sfpCodes.4Unsatisfactory')}</option>
                    </Field>
                  </div>
                </div>
                {formik.errors.rating && (
                  <div className="col-sm-12">
                    <span className="form__form-group-error">{formik.errors.rating}</span>
                  </div>
                )}
              </div>
              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">
                    {t('Rationale')}
                    <span className="error"> *</span>
                  </label>
                  <div>
                    Please be succinct, to the point and the same as in the supervision report. Note
                    that this text will be in the PMR (and publicly disclosed).
                  </div>
                  <Field
                    className="scrollarea"
                    as="textarea"
                    id="rationale"
                    name="rationale"
                    maxLength={5000}
                  />
                </div>
                {formik.errors.rationale && (
                  <div className="col-sm-12">
                    <span className="form__form-group-error">{formik.errors.rationale}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="modal__footer modal__foote--custom-filter">
            <Button key="btnCancel" className="btn-tertiary" type="reset">
              {t('cancel')}
            </Button>
            <Button
              key="btnSave"
              className="btn-primary"
              type="submit"
              disabled={!formik.isValid}
              color="primary"
            >
              {t('send')}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default SprForm;
